import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FieldError} from '@/components/field-error'
import {MicroLabel} from '@/components/period-selector'
import {EmpleadoSelect} from '@/components/plan-trabajo-empleado-select'
import {useResponsablesRoster} from '@/hooks/empresa/identidad/use-employees'
import {
  useCrearAdenda,
  useEditarActividad,
  useQuitarActividad,
  type PlanActividad,
  type PlanTrabajo,
} from '@/hooks/sgsst/plan/use-plan-trabajo'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

type Campo = 'titulo' | 'objetivo' | 'meta'
const CAMPOS: Campo[] = ['titulo', 'objetivo', 'meta']

/** Los meses se editan acá y no en los círculos del cronograma, cuyo clic ya marca la ejecución.
 * Firmado no se edita nada: se ofrece la adenda. */
export function ActividadDetalleDialog({
  businessId,
  plan,
  actividad,
  onClose,
}: {
  businessId: string
  plan: PlanTrabajo
  actividad: PlanActividad
  onClose: () => void
}) {
  const {t} = useTranslation()
  const editable = plan.estado === 'borrador'
  // Pide también los externos: el responsable ya asignado puede serlo.
  const {data: roster} = useResponsablesRoster(businessId, true)
  const editar = useEditarActividad(businessId, plan.id)
  const quitar = useQuitarActividad(businessId, plan.id)
  const adenda = useCrearAdenda(businessId, plan.id)
  const [error, setError] = useState<string | null>(null)
  const [valores, setValores] = useState(() => ({
    titulo: actividad.titulo,
    objetivo: actividad.objetivo,
    meta: actividad.meta,
  }))
  const [meses, setMeses] = useState<number[]>(() => actividad.meses_planeados)
  const [asignadoId, setAsignadoId] = useState(actividad.asignado_employee_id ?? '')
  const [errores, setErrores] = useState<Partial<Record<Campo | 'meses', string>>>({})
  const [guardado, setGuardado] = useState(false)
  const [confirmQuitar, setConfirmQuitar] = useState(false)
  const [nodo, setNodo] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setValores({titulo: actividad.titulo, objetivo: actividad.objetivo, meta: actividad.meta})
    setMeses(actividad.meses_planeados)
    setAsignadoId(actividad.asignado_employee_id ?? '')
    setErrores({})
    setGuardado(false)
    setError(null)
    // only re-sync when the dialog switches actividad — not on every edit.mutate response
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actividad.id])

  const mesesIguales =
    meses.length === actividad.meses_planeados.length && meses.every((m, i) => m === actividad.meses_planeados[i])
  const dirty =
    !mesesIguales ||
    asignadoId !== (actividad.asignado_employee_id ?? '') ||
    CAMPOS.some((c) => valores[c] !== actividad[c])

  function cambiar(campo: Campo, valor: string) {
    setValores((v) => ({...v, [campo]: valor}))
    setErrores((e) => ({...e, [campo]: undefined}))
    setGuardado(false)
  }

  function alternarMes(mes: number) {
    setMeses((prev) => (prev.includes(mes) ? prev.filter((m) => m !== mes) : [...prev, mes].sort((a, b) => a - b)))
    setErrores((e) => ({...e, meses: undefined}))
    setGuardado(false)
  }

  function guardar() {
    const problemas: Partial<Record<Campo | 'meses', string>> = {}
    for (const campo of CAMPOS) {
      const problema = validateRequired(valores[campo])
      if (problema) problemas[campo] = problema
    }
    if (meses.length === 0) problemas.meses = 'planTrabajo.detalle.mesesVacios'
    setErrores(problemas)
    if (Object.keys(problemas).length > 0) return
    setError(null)
    editar.mutate(
      {
        actividadId: actividad.id,
        payload: {
          titulo: valores.titulo,
          objetivo: valores.objetivo,
          meta: valores.meta,
          meses_planeados: meses,
          asignado_employee_id: asignadoId || null,
        },
      },
      {
        onSuccess: () => setGuardado(true),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Ref de callback: Radix monta el contenido después del primer efecto, y sin `contenedor`
          el Escape del `PorQue` cerraría el diálogo entero. */}
      <DialogContent ref={setNodo} data-testid="actividad-detalle-dialog">
        <DialogHeader>
          <DialogTitle>{actividad.titulo}</DialogTitle>
          <DialogDescription>{t('planTrabajo.detalle.titulo')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-3 overflow-y-auto">
          {!editable && <PlanFirmadoAviso adenda={adenda} onCreada={onClose} onError={setError} contenedor={nodo} />}

          <div>
            <MicroLabel>{t('planTrabajo.detalle.tituloCampo')}</MicroLabel>
            {editable ? (
              <>
                <Input
                  maxLength={tope('NOTA')}
                  data-testid="detalle-titulo"
                  className="mt-1"
                  value={valores.titulo}
                  onChange={(e) => cambiar('titulo', e.target.value)}
                  onBlur={() => setErrores((e) => ({...e, titulo: validateRequired(valores.titulo) ?? undefined}))}
                  aria-invalid={!!errores.titulo}
                />
                <FieldError error={errores.titulo} />
              </>
            ) : (
              <p className="mt-1 text-[13.5px] text-foreground">{actividad.titulo}</p>
            )}
          </div>

          <CampoTexto
            campo="objetivo"
            editable={editable}
            valor={valores.objetivo}
            leido={actividad.objetivo}
            error={errores.objetivo}
            onChange={cambiar}
            onBlur={() => setErrores((e) => ({...e, objetivo: validateRequired(valores.objetivo) ?? undefined}))}
          />
          <CampoTexto
            campo="meta"
            editable={editable}
            valor={valores.meta}
            leido={actividad.meta}
            error={errores.meta}
            onChange={cambiar}
            onBlur={() => setErrores((e) => ({...e, meta: validateRequired(valores.meta) ?? undefined}))}
          />

          <div>
            <MicroLabel>{t('planTrabajo.detalle.meses')}</MicroLabel>
            {editable && <Hint className="mt-0.5">{t('planTrabajo.detalle.mesesAyuda')}</Hint>}
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="detalle-meses">
              {MESES.map((label, i) => {
                const mes = i + 1
                const puesto = meses.includes(mes)
                return (
                  <button
                    key={mes}
                    type="button"
                    disabled={!editable}
                    aria-pressed={puesto}
                    data-testid={`detalle-mes-${mes}`}
                    onClick={() => alternarMes(mes)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 font-mono text-[11px] tabular-nums transition-colors',
                      puesto
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input text-muted-foreground',
                      editable ? 'cursor-pointer hover:border-primary' : 'cursor-default opacity-70'
                    )}>
                    {label}
                  </button>
                )
              })}
            </div>
            <FieldError error={errores.meses} />
          </div>

          <div>
            <MicroLabel>{t('planTrabajo.detalle.fuente')}</MicroLabel>
            <p className="mt-1">
              <Tag tone="neutral">{t(`planTrabajo.fuente.${actividad.fuente}`)}</Tag>
            </p>
          </div>
          {actividad.cita && (
            <div>
              <MicroLabel>{t('planTrabajo.detalle.cita')}</MicroLabel>
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">{actividad.cita}</p>
            </div>
          )}

          <div>
            <MicroLabel>{t('planTrabajo.detalle.asignar')}</MicroLabel>
            <div className="mt-1">
              <EmpleadoSelect
                roster={roster ?? []}
                value={asignadoId || null}
                placeholder={t('planTrabajo.detalle.sinAsignar')}
                testId="detalle-asignado"
                disabled={!editable}
                onPick={(id) => {
                  setAsignadoId(id ?? '')
                  setGuardado(false)
                }}
              />
            </div>
          </div>

          {editable && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                data-testid="detalle-guardar"
                disabled={!dirty || editar.isPending}
                onClick={guardar}>
                {editar.isPending ? t('common.saving') : t('planTrabajo.detalle.guardar')}
              </Button>
              {guardado && !dirty && (
                <Tag tone="green" data-testid="detalle-guardado">
                  {t('planTrabajo.detalle.guardadoOk')}
                </Tag>
              )}
            </div>
          )}
          {error && (
            <p className="text-[12.5px] text-destructive" data-testid="detalle-error">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          {editable && (
            <button
              type="button"
              className="text-[12px] text-destructive underline"
              data-testid="detalle-quitar"
              onClick={() => setConfirmQuitar(true)}>
              {t('planTrabajo.detalle.quitar')}
            </button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('planTrabajo.detalle.cerrar')}
          </Button>
        </DialogFooter>
        <ConfirmDialog
          open={confirmQuitar}
          onOpenChange={setConfirmQuitar}
          title={t('planTrabajo.detalle.quitarConfirmTitulo')}
          description={t('planTrabajo.detalle.quitarConfirmBody', {titulo: actividad.titulo})}
          confirmLabel={t('planTrabajo.detalle.quitarConfirmar')}
          cancelLabel={t('common.cancel')}
          pending={quitar.isPending}
          onConfirm={() =>
            quitar.mutate(actividad.id, {
              onSuccess: () => {
                setConfirmQuitar(false)
                onClose()
              },
              onError: (err) => {
                setConfirmQuitar(false)
                setError(translateApiError(err))
              },
            })
          }
        />
      </DialogContent>
    </Dialog>
  )
}

function CampoTexto({
  campo,
  editable,
  valor,
  leido,
  error,
  onChange,
  onBlur,
}: {
  campo: Campo
  editable: boolean
  valor: string
  leido: string
  error?: string
  onChange: (campo: Campo, valor: string) => void
  onBlur: () => void
}) {
  const {t} = useTranslation()
  return (
    <div>
      <MicroLabel>{t(`planTrabajo.detalle.${campo}`)}</MicroLabel>
      {editable ? (
        <>
          <Textarea
            maxLength={tope('NOTA')}
            data-testid={`detalle-${campo}`}
            className="mt-1 text-[13px]"
            minRows={2}
            value={valor}
            onChange={(e) => onChange(campo, e.target.value)}
            onBlur={onBlur}
            aria-invalid={!!error}
          />
          <FieldError error={error} />
        </>
      ) : (
        <p className="mt-1 text-[13.5px] text-foreground">{leido}</p>
      )}
    </div>
  )
}

function PlanFirmadoAviso({
  adenda,
  onCreada,
  onError,
  contenedor,
}: {
  adenda: ReturnType<typeof useCrearAdenda>
  onCreada: () => void
  onError: (mensaje: string) => void
  contenedor: HTMLElement | null
}) {
  const {t} = useTranslation()
  return (
    <div className="surface border-l-4 border-primary p-3" data-testid="detalle-plan-firmado">
      <p className="text-[13px] font-medium text-foreground">{t('planTrabajo.detalle.firmadoTitulo')}</p>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground">
        {t('planTrabajo.detalle.firmadoCuerpo')}
        <PorQue
          label={t('planTrabajo.detalle.firmadoPorque')}
          testId="detalle-firmado-porque"
          contenidoTestId="detalle-firmado-porque-contenido"
          contenedor={contenedor}>
          {t('planTrabajo.detalle.firmadoPorqueCuerpo')}
        </PorQue>
      </p>
      <Button
        size="sm"
        className="mt-2"
        data-testid="detalle-crear-adenda"
        disabled={adenda.isPending}
        onClick={() =>
          adenda.mutate(undefined, {
            onSuccess: onCreada,
            onError: (err) => onError(translateApiError(err)),
          })
        }>
        {adenda.isPending ? t('common.saving') : t('planTrabajo.detalle.firmadoCrearAdenda')}
      </Button>
    </div>
  )
}
